import Modal from '@material-ui/core/Modal';
import { observer, useLocalStore } from 'mobx-react-lite';
import React, { useContext } from 'react';
import { ChatsContext } from '../../contexts/Chats';
import { UsersContext } from '../../contexts/Users';
import { loadSessionState, saveSessionState } from '../../utils/sessionState';
import { CLASSROOM_SORTS, sortClassrooms } from '../../utils/sortClassrooms';
import ClassroomCard from './ClassroomCard';
import CreateClassroomForm from './CreateClassroomForm';
import * as CSS from './elements/Classrooms';

const FILTERS_KEY = 'classroomFilters';
const ALL = 'all';
const MINE = 'mine';
const DEFAULT_FILTERS = {
  sort: CLASSROOM_SORTS.teacher,
  teacher: ALL,
  student: ALL,
};

const byDisplayName = (a, b) => a.displayName.localeCompare(b.displayName);

const Classrooms = observer(() => {
  const chatsStore = useContext(ChatsContext);
  const usersStore = useContext(UsersContext);
  const loggedInUser = usersStore.getLoggedInUser();

  const store = useLocalStore(() => ({
    searchTerm: '',
    isModalOpen: false,
    filters: loadSessionState(FILTERS_KEY, DEFAULT_FILTERS),
    setModal: (open) => {
      store.isModalOpen = open;
    },
    setFilter(name, value) {
      store.filters = { ...store.filters, [name]: value };
      saveSessionState(FILTERS_KEY, store.filters);
    },
    clearFilters() {
      store.filters = { ...DEFAULT_FILTERS, sort: store.filters.sort };
      saveSessionState(FILTERS_KEY, store.filters);
    },
  }));

  const handleSearchChange = (e) => {
    store.searchTerm = e.target.value;
  };

  const { sort, teacher, student } = store.filters;
  // classrooms always follow the category selected in the nav (F8)
  const categoryId = loggedInUser.selectedCategory;

  // teacher and student options come from the classrooms in the chosen category
  const classroomsInCategory = chatsStore.groupChats.filter(
    (chat) => chat.category === categoryId
  );
  const teachers = [
    ...new Set(classroomsInCategory.map(({ teacherId }) => teacherId)),
  ]
    .filter((id) => id && id !== loggedInUser.id)
    .map((id) => usersStore.getUserById(id))
    .filter(Boolean)
    .sort(byDisplayName);
  const students = [
    ...new Set(
      classroomsInCategory.flatMap(({ participants }) => [...participants])
    ),
  ]
    .map((id) => usersStore.getUserById(id))
    .filter((user) => user?.role === 'student')
    .sort(byDisplayName);

  const getClassrooms = (active) =>
    sortClassrooms(
      chatsStore.getGroupChats({
        active,
        searchTerm: store.searchTerm,
        categoryId,
        teacherId:
          teacher === ALL ? null : teacher === MINE ? loggedInUser.id : teacher,
        studentId: student === ALL ? null : student,
      }),
      {
        sort,
        loggedInUserId: loggedInUser.id,
        getTeacherName: (id) => usersStore.getUserById(id)?.displayName,
      }
    );
  const activeClassrooms = getClassrooms(true);
  const inactiveClassrooms = getClassrooms(false);

  const isFiltered = teacher !== ALL || student !== ALL;

  return (
    <CSS.StaffContainer data-testid="Classrooms">
      <CSS.StaffRow>
        <CSS.Inline>
          <CSS.StaffHeader>Classrooms</CSS.StaffHeader>
          <CSS.AlignButton>
            <CSS.CreateButton onClick={() => store.setModal(true)}>
              Create <CSS.PlusIcon />
            </CSS.CreateButton>
          </CSS.AlignButton>
        </CSS.Inline>
        <CSS.SearchField
          onChange={handleSearchChange}
          data-testid="Classrooms-search"
          placeholder="Search Classrooms"
        ></CSS.SearchField>
      </CSS.StaffRow>
      <CSS.FilterBar aria-label="Sort and filter classrooms">
        <CSS.StaffSelect
          select
          id="classrooms-sort"
          label="Sort by"
          value={sort}
          onChange={(e) => store.setFilter('sort', e.target.value)}
        >
          <CSS.StaffMenuItem value={CLASSROOM_SORTS.teacher}>
            Teacher (mine first)
          </CSS.StaffMenuItem>
          <CSS.StaffMenuItem value={CLASSROOM_SORTS.name}>
            Name (A–Z)
          </CSS.StaffMenuItem>
        </CSS.StaffSelect>
        <CSS.StaffSelect
          select
          id="classrooms-teacher"
          label="Teacher"
          value={teacher}
          onChange={(e) => store.setFilter('teacher', e.target.value)}
        >
          <CSS.StaffMenuItem value={ALL}>All teachers</CSS.StaffMenuItem>
          <CSS.StaffMenuItem value={MINE}>Mine</CSS.StaffMenuItem>
          {teachers.map(({ id, displayName }) => (
            <CSS.StaffMenuItem key={id} value={id}>
              {displayName}
            </CSS.StaffMenuItem>
          ))}
        </CSS.StaffSelect>
        <CSS.StaffSelect
          select
          id="classrooms-student"
          label="Student"
          value={student}
          onChange={(e) => store.setFilter('student', e.target.value)}
        >
          <CSS.StaffMenuItem value={ALL}>All students</CSS.StaffMenuItem>
          {students.map(({ id, displayName }) => (
            <CSS.StaffMenuItem key={id} value={id}>
              {displayName}
            </CSS.StaffMenuItem>
          ))}
        </CSS.StaffSelect>
        {isFiltered && (
          <CSS.Button
            type="button"
            $variant="secondary"
            $size="sm"
            onClick={store.clearFilters}
          >
            Clear filters
          </CSS.Button>
        )}
      </CSS.FilterBar>
      <CSS.StaffRow>
        <CSS.StaffHeaderGroup>
          <CSS.StaffHeaderCount>{activeClassrooms.length}</CSS.StaffHeaderCount>
          <CSS.StaffSubHeader>Active</CSS.StaffSubHeader>
        </CSS.StaffHeaderGroup>
      </CSS.StaffRow>
      <CSS.CardList data-testid="Classrooms-active">
        {activeClassrooms.map((groupChat) => (
          <ClassroomCard key={groupChat.id} groupChatId={groupChat.id} />
        ))}
      </CSS.CardList>
      <CSS.StaffHeaderGroup>
        <CSS.StaffHeaderCount>{inactiveClassrooms.length}</CSS.StaffHeaderCount>
        <CSS.StaffSubHeader>Inactive</CSS.StaffSubHeader>
      </CSS.StaffHeaderGroup>
      <CSS.CardList data-testid="Classrooms-inactive">
        {inactiveClassrooms.map((groupChat) => (
          <ClassroomCard key={groupChat.id} groupChatId={groupChat.id} />
        ))}
      </CSS.CardList>
      <Modal
        data-testid="create-classroom-modal"
        open={store.isModalOpen}
        onClose={() => store.setModal(false)}
        aria-labelledby="create-classroom-modal"
        aria-describedby="create-classroom-modal"
      >
        <CreateClassroomForm closeModal={() => store.setModal(false)} />
      </Modal>
    </CSS.StaffContainer>
  );
});

export default Classrooms;
